import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/use-user';
import { useToast } from '@/hooks/use-toast';

interface PropertyRatingCompactProps {
  propertyId: string;
}

interface AverageRating {
  average: number;
  total: number;
}

export const PropertyRatingCompact: React.FC<PropertyRatingCompactProps> = ({ propertyId }) => {
  const { user } = useUser();
  const { toast } = useToast();
  const [averageRating, setAverageRating] = useState<AverageRating>({
    average: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userRating, setUserRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);

  useEffect(() => {
    fetchRatingsSummary();
  }, [propertyId]);

  useEffect(() => {
    if (user) {
      fetchUserRating();
    }
  }, [user, propertyId]);

  const fetchRatingsSummary = async () => {
    try {
      setLoading(true);
      
      const { data: ratingsData, error } = await supabase
        .from('public_property_ratings')
        .select('rating')
        .eq('property_id', propertyId);

      if (error) throw error;


      const ratings = ratingsData || [];
      
      if (ratings.length > 0) {
        const total = ratings.length;
        const sum = ratings.reduce((acc, rating) => acc + (rating.rating ?? 0), 0);
        const average = sum / total;
        
        setAverageRating({ average, total });
      } else {
        setAverageRating({ average: 0, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching ratings summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRating = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('property_ratings')
        .select('rating')
        .eq('property_id', propertyId)
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setUserRating(data.rating ?? 0);
      }
    } catch (error) {
      console.error('Error fetching user rating:', error);
    }
  };

  const submitRating = async (rating: number) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to rate this property",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);

      const ratingData = {
        property_id: propertyId,
        user_id: user.id,
        rating: rating,
        review: null,
      };

      if (userRating > 0) {
        // Update existing rating
        const { error } = await supabase
          .from('property_ratings')
          .update(ratingData)
          .eq('property_id', propertyId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Create new rating
        const { error } = await supabase
          .from('property_ratings')
          .insert(ratingData);

        if (error) throw error;
      }

      setUserRating(rating);
      toast({
        title: "Rating Submitted",
        description: "Thank you for your rating!",
      });
      
      // Refresh the summary
      fetchRatingsSummary();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: "Error",
        description: "Failed to submit rating",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating: number, interactive = false) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isFilled = starValue <= Math.round(rating);
      const isHovered = interactive && hoveredRating >= starValue;
      const isUserRated = interactive && userRating >= starValue;
      
      return (
        <button
          key={index}
          type="button"
          disabled={submitting || !interactive}
          className={`h-4 w-4 transition-colors ${
            interactive 
              ? 'hover:text-accent cursor-pointer disabled:cursor-not-allowed' 
              : 'cursor-default'
          }`}
          onClick={() => interactive && submitRating(starValue)}
          onMouseEnter={() => interactive && setHoveredRating(starValue)}
          onMouseLeave={() => interactive && setHoveredRating(0)}
        >
          <Star 
            className={`h-4 w-4 transition-all ${
              interactive
                ? (isHovered || isUserRated) 
                  ? 'fill-accent text-accent' 
                  : 'text-muted-foreground'
                : isFilled 
                  ? 'fill-accent text-accent' 
                  : 'text-muted-foreground'
            }`}
          />
        </button>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="h-4 w-4 bg-muted rounded animate-pulse" />
          ))}
        </div>
        <div className="h-4 w-12 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (averageRating.total === 0) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <div className="flex gap-1">
          {renderStars(userRating, user ? true : false)}
        </div>
        <span className="text-sm">No reviews</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1" title={user ? "Click to rate this property" : "Sign in to rate this property"}>
        {renderStars(userRating > 0 ? userRating : averageRating.average, user ? true : false)}
      </div>
      <div className="flex items-center gap-1 text-sm">
        <span className="font-medium text-foreground">
          {averageRating.total > 0 ? averageRating.average.toFixed(1) : '0.0'}
        </span>
        <span className="text-muted-foreground">
          ({averageRating.total} review{averageRating.total !== 1 ? 's' : ''})
        </span>
      </div>
    </div>
  );
};